import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Trash2, FileText, AlertTriangle, X, ChevronRight, ChevronLeft, Download, Upload, Copy, Check, FileCheck, FileClock, Files, Users, CheckCircle, Send, MoreHorizontal, RefreshCw, Ban, FileX, History, Clock, Pencil, ExternalLink } from 'lucide-react';
import VertragPreviewModal from '../components/VertragPreviewModal';

const API = import.meta.env.VITE_API_URL || '';

const STATUS_MAP = {
  entwurf: { label: 'Entwurf', bg: 'bg-gray-100', text: 'text-gray-700' },
  gesendet: { label: 'Gesendet', bg: 'bg-blue-50', text: 'text-blue-700' },
  unterschrieben: { label: 'Unterschrieben', bg: 'bg-green-50', text: 'text-green-700' },
  resend_pending: { label: 'Resend pending', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  storniert: { label: 'Storniert', bg: 'bg-amber-50', text: 'text-amber-700' },
};

const DV_STATUS = {
  'Laufend': 'bg-emerald-50 text-emerald-700',
  'Entwurf': 'bg-blue-50 text-blue-700',
  'Abgeschlossen': 'bg-gray-100 text-gray-600',
  'Storniert': 'bg-red-50 text-red-600',
};

const TYP_LABELS = {
  vollzeit: 'Vollzeit', teilzeit: 'Teilzeit', geringfuegig: 'Geringfügig',
  praktikum: 'Praktikum', sonstig: 'Sonstig',
};

const DATE_FIELDS = ['geburtsdatum', 'eintritt_datum', 'probezeit_bis', 'datum_heute'];

// Backend mappt Legacy → canonical automatisch (LEGACY_ALIAS). Step-2 zeigt nur canonical Felder,
// damit User nicht denselben Wert zweimal eingeben müssen.
const LEGACY_ALIAS = {
  eintrittsdatum: 'eintritt_datum',
  brutto_monatlich: 'brutto',
  vertragsdatum: 'datum_heute',
  stunden_pro_woche: 'wochenstunden',
};

// Rollen, die die neuen Admin-Aktionen (unsign/resend/stornieren/DELETE/audit) sehen dürfen.
// Muss mit backend adminOnly ['admin'] + ROLE_ALIAS admin→administrator konsistent bleiben.
const ADMIN_ROLES = ['admin', 'administrator'];

export default function VertraegePage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.rolle);
  const [tab, setTab] = useTabParam('dienstvertraege');

  // DV state
  const [dvList, setDvList] = useState([]);
  const [dvLoading, setDvLoading] = useState(true);
  const [dvFilter, setDvFilter] = useState({ ma: '', aktiv: '' });
  const [mitarbeiterList, setMitarbeiterList] = useState([]);
  const [showDvCreate, setShowDvCreate] = useState(false);
  const [dvForm, setDvForm] = useState({ mitarbeiter_id: '', bezeichnung: '', vertrag_typ: 'vollzeit', start_datum: '', end_datum: '', brutto_monatlich: '', stunden_pro_woche: '', position: '', notiz: '', ist_zusatzvereinbarung: false, __override_grund: '' });
  const [dvFile, setDvFile] = useState(null);
  const [dvCreating, setDvCreating] = useState(false);
  const [dvError, setDvError] = useState('');

  // Digital contracts state
  const [vertraege, setVertraege] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [standardVars, setStandardVars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  // #1205: createStep 0 = Vollständigkeits-Check (only if MA selected & incomplete),
  // 1 = Template+Person, 2 = Read-only Variablen-Vorschau, 3 = PDF-Preview vor Generate.
  const [createStep, setCreateStep] = useState(1);
  const [createForm, setCreateForm] = useState({ template_id: '', mitarbeiter_id: '', bewerber_id: '', notiz: '' });
  const [variablenWerte, setVariablenWerte] = useState({});
  const [urlaubsInfo, setUrlaubsInfo] = useState(null);       // {aufgebaut_tage, anspruch_voll_tage, in_wartefrist, wartefrist_bis, ...}
  const [vollstaendigkeit, setVollstaendigkeit] = useState(null); // {ok, fehlend, fehlend_labels, ma_name, ma_id}
  const [loadingVars, setLoadingVars] = useState(false);
  const [bewerberList, setBewerberList] = useState([]);
  const [creating, setCreating] = useState(false);

  // #1205 Preview-Modal (3 Aufruf-Stellen: Anlage-Wizard, Vorlagen-Liste, Vertrags-Liste).
  const [previewModal, setPreviewModal] = useState(null); // { mode, endpoint, body?, title?, downloadFileName? } | null
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: '', typ: 'vollzeit' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // DG signature
  const [dgSignature, setDgSignature] = useState({ vorhanden: false });
  const [dgUploading, setDgUploading] = useState(false);
  const [dgCacheBust, setDgCacheBust] = useState(0);

  // Confirm dialog
  const [confirmAction, setConfirmAction] = useState(null);
  // #887: Grund-Input + Tipp-Bestätigung für destruktive Admin-Aktionen.
  // Getrennt vom confirmAction-State, damit der Modal-State beim Öffnen immer sauber resettet.
  const [grundInput, setGrundInput] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [actionBusy, setActionBusy] = useState(false);

  // Dropdown-Menu offen (ein Vertrag auf einmal); Ref für Outside-Click.
  // #887 Nachtrag: Portal statt absolute-in-td — umgeht table-wrapper overflow:hidden.
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, placement: 'bottom' });
  const menuRef = useRef(null);
  const menuBtnRefs = useRef({});

  const openAdminMenu = (vId, btnEl) => {
    if (openMenuId === vId) { setOpenMenuId(null); return; }
    const rect = btnEl.getBoundingClientRect();
    const menuW = 240;
    const menuH = 260; // Obergrenze; Auto-Flip wenn unten < menuH.
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement = spaceBelow < menuH ? 'top' : 'bottom';
    const top = placement === 'bottom' ? rect.bottom + 4 : rect.top - 4;
    const left = Math.max(8, Math.min(window.innerWidth - menuW - 8, rect.right - menuW));
    setMenuPos({ top, left, placement });
    setOpenMenuId(vId);
  };

  // Audit-Log-Slideover.
  const [auditVertrag, setAuditVertrag] = useState(null);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Toast for copy feedback
  const [toast, setToast] = useState('');

  // Existing DV for warning
  const [activeDvWarning, setActiveDvWarning] = useState(null);

  const fetchDV = useCallback(async () => {
    setDvLoading(true);
    try {
      const params = new URLSearchParams();
      if (dvFilter.ma) params.set('mitarbeiter_id', dvFilter.ma);
      if (dvFilter.aktiv) params.set('ist_aktiv', dvFilter.aktiv);
      const res = await fetch(`${API}/api/dienstvertraege?${params}`, { credentials: 'include' });
      const data = await res.json();
      setDvList(data.dienstvertraege || []);
    } catch { /* */ }
    setDvLoading(false);
  }, [dvFilter]);

  const fetchDigital = useCallback(async () => {
    try {
      const [vRes, tRes] = await Promise.all([
        fetch(`${API}/api/vertraege`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/vertraege/templates`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setVertraege(vRes.vertraege || []);
      setTemplates(tRes.templates || []);
      setStandardVars(tRes.standard_variablen || []);
    } catch { /* */ }
    setLoading(false);
  }, []);

  const loadMitarbeiter = async () => {
    try {
      // #1021 BR-14: include_former=true → ausgetretene MAs im Dropdown sichtbar (für Nach-Austritt-Vertrag).
      const res = await fetch(`${API}/api/mitarbeiter?include_former=true`, { credentials: 'include' });
      const data = await res.json();
      setMitarbeiterList(Array.isArray(data) ? data : data.mitarbeiter || []);
    } catch { /* */ }
  };

  // #1021 BR-14: Lookup austritt_datum für gewählten MA (Trigger für Override-Grund-Pflichtfeld).
  const getSelectedMaAustritt = (maId) => {
    if (!maId) return null;
    const ma = mitarbeiterList.find(m => String(m.id) === String(maId));
    return ma?.austritt_datum || null;
  };

  useEffect(() => { fetchDV(); }, [fetchDV]);
  useEffect(() => { fetchDigital(); }, [fetchDigital]);
  useEffect(() => { loadMitarbeiter(); }, []);

  // #1209: Auto-Open Wizard via URL-Params ?ma=<id>&open=1.
  // Triggered z.B. von MitarbeiterDetail Aktionen-Dropdown → Neuer Vertrag.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (tab !== 'digital') return;
    if (mitarbeiterList.length === 0) return;
    if (loading) return;  // wartet auf templates
    const params = new URLSearchParams(window.location.search);
    if (params.get('open') !== '1') return;
    const maParam = params.get('ma');
    if (!maParam) return;
    autoOpenedRef.current = true;
    setCreateForm({ template_id: '', mitarbeiter_id: String(maParam), bewerber_id: '', notiz: '' });
    setVariablenWerte({});
    setUrlaubsInfo(null);
    setVollstaendigkeit(null);
    setCreateStep(1);
    loadDropdowns();
    setShowCreate(true);
  }, [tab, mitarbeiterList, loading]);
  // Dropdown schließt bei Klick außerhalb oder Scroll/Resize (Portal-Position sonst stale).
  useEffect(() => {
    if (openMenuId === null) return;
    const onClick = (e) => {
      const btn = menuBtnRefs.current[openMenuId];
      if (menuRef.current && !menuRef.current.contains(e.target) && (!btn || !btn.contains(e.target))) {
        setOpenMenuId(null);
      }
    };
    const onClose = () => setOpenMenuId(null);
    document.addEventListener('mousedown', onClick);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [openMenuId]);

  // DV create
  const openDvCreate = () => {
    setDvForm({ mitarbeiter_id: '', bezeichnung: '', vertrag_typ: 'vollzeit', start_datum: '', end_datum: '', brutto_monatlich: '', stunden_pro_woche: '', position: '', notiz: '', ist_zusatzvereinbarung: false, __override_grund: '' });
    setDvFile(null);
    setActiveDvWarning(null);
    setDvError('');
    setShowDvCreate(true);
  };

  const checkActiveDv = (maId) => {
    if (!maId) { setActiveDvWarning(null); return; }
    const active = dvList.find(d => String(d.mitarbeiter_id) === String(maId) && d.ist_aktiv);
    setActiveDvWarning(active || null);
  };

  const handleDvCreate = async () => {
    setDvError('');
    if (!dvForm.mitarbeiter_id || !dvForm.bezeichnung || !dvForm.start_datum) {
      setDvError(t('dienstvertrag.createMissingBase', 'Bitte Mitarbeiter, Bezeichnung und Startdatum ausfüllen.'));
      return;
    }
    // #1021 BR-14: Bei ausgetretenem MA Override-Grund Pflicht (10..1000 Zeichen, Backend rejected sonst 400/403).
    const austritt = getSelectedMaAustritt(dvForm.mitarbeiter_id);
    if (austritt) {
      const grund = (dvForm.__override_grund || '').trim();
      if (grund.length < 10) { setDvError(t('dienstvertrag.overrideReasonTooShort') || 'Bitte Grund angeben (mind. 10 Zeichen).'); return; }
      if (grund.length > 1000) { setDvError(t('dienstvertrag.overrideReasonTooLong') || 'Grund max. 1000 Zeichen.'); return; }
    }
    setDvCreating(true);
    try {
      const fd = new FormData();
      Object.entries(dvForm).forEach(([k, v]) => {
        if (k === '__override_grund' && !austritt) return; // nur senden wenn nötig
        if (k === 'ist_zusatzvereinbarung') { fd.append(k, v ? 'true' : 'false'); return; }
        if (v) fd.append(k, v);
      });
      if (dvFile) fd.append('file', dvFile);
      const res = await fetch(`${API}/api/dienstvertraege`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Fehler');
      setShowDvCreate(false);
      fetchDV();
    } catch (err) { setDvError(err.message); }
    setDvCreating(false);
  };

  // ═══ Digital contract handlers ═══
  const loadDropdowns = async () => {
    try {
      const bRes = await fetch(`${API}/api/recruiting/bewerber`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ bewerber: [] }));
      setBewerberList(bRes.bewerber || []);
    } catch { /* */ }
  };

  const openCreate = () => {
    setCreateForm({ template_id: '', mitarbeiter_id: '', bewerber_id: '', notiz: '' });
    setVariablenWerte({});
    setUrlaubsInfo(null);
    setVollstaendigkeit(null);
    setCreateStep(1);
    loadDropdowns();
    setShowCreate(true);
  };

  // Get the active template variables list. Collapse Legacy → canonical und dedupe,
  // damit User nicht "eintritt_datum" + "eintrittsdatum" doppelt ausfüllen.
  const getTemplateVars = () => {
    if (!createForm.template_id) return standardVars;
    const tmpl = templates.find(t => t.id === Number(createForm.template_id));
    const raw = (tmpl?.variablen?.length > 0) ? tmpl.variablen : standardVars;
    const seen = new Set();
    const result = [];
    for (const v of raw) {
      const rawKey = v.key || v;
      const canonicalKey = LEGACY_ALIAS[rawKey] || rawKey;
      if (seen.has(canonicalKey)) continue;
      seen.add(canonicalKey);
      const std = standardVars.find(s => s.key === canonicalKey);
      result.push({ key: canonicalKey, label: std?.label || v.label || canonicalKey });
    }
    return result;
  };

  // Step 1 → (optional Step 0 Vollständigkeit) → Step 2: Load MA/Bewerber data
  // #1205: Backend liefert jetzt {werte, urlaubs_info, vollstaendigkeit, standard_variablen}.
  const goToStep2 = async () => {
    if (!createForm.template_id || (!createForm.mitarbeiter_id && !createForm.bewerber_id)) return;
    setLoadingVars(true);

    const heute = new Date().toISOString().split('T')[0];
    let werte = { datum_heute: heute, urlaubstage: '25', standort: 'Frikusweg 10, 8141 Unterpremstätten' };
    let urlaubs = null;
    let vollst = null;

    if (createForm.mitarbeiter_id) {
      try {
        const res = await fetch(`${API}/api/vertraege/ma-variablen/${createForm.mitarbeiter_id}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          werte = data.werte || werte;
          urlaubs = data.urlaubs_info || null;
          vollst = data.vollstaendigkeit || null;
        }
      } catch { /* */ }
    } else if (createForm.bewerber_id) {
      const bew = bewerberList.find(b => String(b.id) === createForm.bewerber_id);
      if (bew) {
        werte.vorname = bew.vorname || '';
        werte.nachname = bew.nachname || '';
      }
    }

    setVariablenWerte(werte);
    setUrlaubsInfo(urlaubs);
    setVollstaendigkeit(vollst);
    setLoadingVars(false);
    // Wenn MA gewählt UND Stammdaten unvollständig → Step 0 (Block + Deep-Link).
    if (createForm.mitarbeiter_id && vollst && vollst.ok === false) {
      setCreateStep(0);
    } else {
      setCreateStep(2);
    }
  };

  // Step 0 → Step 1: User repariert Stammdaten und kommt zurück.
  const reloadAfterStammdatenFix = async () => {
    await goToStep2();
  };

  const handleGenerate = async () => {
    if (!createForm.template_id || (!createForm.mitarbeiter_id && !createForm.bewerber_id)) return;
    setCreating(true);
    try {
      const body = {
        template_id: Number(createForm.template_id),
        variablen_werte: variablenWerte,
        notiz: createForm.notiz || undefined,
      };
      if (createForm.mitarbeiter_id) body.mitarbeiter_id = Number(createForm.mitarbeiter_id);
      if (createForm.bewerber_id) body.bewerber_id = Number(createForm.bewerber_id);
      const res = await fetch(`${API}/api/vertraege/generieren`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Fehler');
      setShowCreate(false);
      fetchDigital();
    } catch (err) { alert(err.message); }
    setCreating(false);
  };

  const handleSend = async (id, kanal) => {
    setConfirmAction({ type: 'send', id, kanal, message: t('vertrag.sendConfirm') });
  };

  // #887: openAdminAction öffnet das Confirm-Modal mit Pflicht-Grund und (bei destruktiven Aktionen)
  // einer Tipp-Bestätigung. Der Modal-Content (Titel, Warnung, Confirm-Phrase) ergibt sich aus `type`.
  const openAdminAction = (type, vertrag) => {
    setGrundInput('');
    setConfirmPhrase('');
    setOpenMenuId(null);
    const name = vertrag.ma_vorname ? `${vertrag.ma_vorname} ${vertrag.ma_nachname}` : vertrag.bew_vorname ? `${vertrag.bew_vorname} ${vertrag.bew_nachname}` : '—';
    setConfirmAction({ type, id: vertrag.id, vertrag, name });
  };

  const handleDelete = (vertrag) => {
    // Backend fordert seit #887 Pflicht-Grund für DELETE. Nur Entwürfe sind hart löschbar.
    openAdminAction('deleteVertrag', vertrag);
  };

  const openAuditLog = async (vertrag) => {
    setOpenMenuId(null);
    setAuditVertrag(vertrag);
    setAuditEntries([]);
    setAuditLoading(true);
    try {
      const res = await fetch(`${API}/api/vertraege/${vertrag.id}/audit-log`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setAuditEntries(data.eintraege || []);
      else alert(data.error || 'Fehler beim Laden');
    } catch (err) { alert(err.message); }
    setAuditLoading(false);
  };

  const handleDeleteTemplate = (tmpl) => {
    setConfirmAction({ type: 'deleteTemplate', id: tmpl.id, name: tmpl.name, message: t('vertrag.deleteTemplateConfirm', { name: tmpl.name }) });
  };

  const handleToggleTemplate = async (id, aktiv) => {
    try {
      await fetch(`${API}/api/vertraege/templates/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ aktiv }),
      });
      fetchDigital();
    } catch { /* */ }
  };

  // Admin-Aktionen (deleteVertrag/unsign/resend/stornieren) brauchen Pflicht-Grund + Tipp-Bestätigung.
  // Liefert das Confirm-Phrase, das der User tippen muss — leer = keine Phrase nötig.
  const getConfirmPhrase = (type) => {
    if (type === 'deleteVertrag') return 'LOESCHEN';
    if (type === 'stornieren') return 'STORNIEREN';
    if (type === 'unsign' || type === 'unsignAndResend') return 'UNTERSCHRIFT LOESCHEN';
    return '';
  };

  const isAdminActionType = (type) => ['deleteVertrag', 'unsign', 'unsignAndResend', 'resend', 'stornieren'].includes(type);

  const executeConfirm = async () => {
    if (!confirmAction) return;

    // Pflicht-Grund + Tipp-Bestätigung bei Admin-Aktionen prüfen.
    if (isAdminActionType(confirmAction.type)) {
      if (grundInput.trim().length < 3) {
        alert(t('vertrag.grundPflicht') || 'Grund ist erforderlich (mindestens 3 Zeichen)');
        return;
      }
      const phrase = getConfirmPhrase(confirmAction.type);
      if (phrase && confirmPhrase.trim().toUpperCase() !== phrase) {
        alert((t('vertrag.phrasePflicht') || 'Bitte tippe zur Bestätigung:') + ' ' + phrase);
        return;
      }
    }

    setActionBusy(true);
    try {
      if (confirmAction.type === 'send') {
        const res = await fetch(`${API}/api/vertraege/${confirmAction.id}/senden`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          credentials: 'include', body: JSON.stringify({ kanal: confirmAction.kanal }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Fehler');
        if (data.sms_error && !data.sms) {
          alert(`${t('vertrag.partialSend') || 'Teilweise gesendet'}: ${data.sms_error}`);
        }
      } else if (confirmAction.type === 'deleteVertrag') {
        const res = await fetch(`${API}/api/vertraege/${confirmAction.id}`, {
          method: 'DELETE', headers: { 'Content-Type': 'application/json' },
          credentials: 'include', body: JSON.stringify({ grund: grundInput.trim() }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Fehler');
      } else if (confirmAction.type === 'deleteTemplate') {
        await fetch(`${API}/api/vertraege/templates/${confirmAction.id}`, { method: 'DELETE', credentials: 'include' });
      } else if (confirmAction.type === 'unsign' || confirmAction.type === 'unsignAndResend') {
        const unsignRes = await fetch(`${API}/api/vertraege/${confirmAction.id}/unterschrift-loeschen`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          credentials: 'include', body: JSON.stringify({ grund: grundInput.trim() }),
        });
        const unsignData = await unsignRes.json().catch(() => ({}));
        if (!unsignRes.ok) throw new Error(unsignData.error || 'Fehler');

        // Chain-Aktion: direkt neuen Link versenden.
        if (confirmAction.type === 'unsignAndResend') {
          const resendRes = await fetch(`${API}/api/vertraege/${confirmAction.id}/resend`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            credentials: 'include', body: JSON.stringify({ grund: `Auto-Resend nach Unterschrift-Löschung: ${grundInput.trim()}` }),
          });
          const resendData = await resendRes.json().catch(() => ({}));
          if (!resendRes.ok) throw new Error(resendData.error || 'Fehler beim Versand nach Löschung');
          if (resendData.sms_error && !resendData.sms) {
            alert(`${t('vertrag.partialSend') || 'Teilweise gesendet'}: ${resendData.sms_error}`);
          }
        }
      } else if (confirmAction.type === 'resend') {
        const res = await fetch(`${API}/api/vertraege/${confirmAction.id}/resend`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          credentials: 'include', body: JSON.stringify({ grund: grundInput.trim() }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Fehler');
        if (data.sms_error && !data.sms) {
          alert(`${t('vertrag.partialSend') || 'Teilweise gesendet'}: ${data.sms_error}`);
        }
      } else if (confirmAction.type === 'stornieren') {
        const res = await fetch(`${API}/api/vertraege/${confirmAction.id}/stornieren`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          credentials: 'include', body: JSON.stringify({ grund: grundInput.trim() }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Fehler');
      }
      fetchDigital();
      setConfirmAction(null);
      setGrundInput('');
      setConfirmPhrase('');
    } catch (err) {
      alert(err.message);
    }
    setActionBusy(false);
  };

  const handleUploadTemplate = async () => {
    if (!uploadForm.name || !uploadFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('name', uploadForm.name);
      fd.append('typ', uploadForm.typ);
      fd.append('template', uploadFile);
      fd.append('variablen', JSON.stringify([]));
      const res = await fetch(`${API}/api/vertraege/templates`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Fehler');
      const data = await res.json();
      setShowUpload(false); setUploadFile(null); setUploadForm({ name: '', typ: 'vollzeit' }); fetchDigital();
      const parts = [];
      if (data.erkannte_variablen?.length > 0) parts.push(`${t('vertrag.erkannteVariablen')}: ${data.erkannte_variablen.join(', ')}`);
      if (data.erkannte_anchors?.length > 0) parts.push(`Signatur-Platzhalter: ${data.erkannte_anchors.join(', ')}`);
      if (data.warnings?.length > 0) parts.push('\n⚠ ' + data.warnings.map(w => w.message).join('\n⚠ '));
      if (parts.length) alert(parts.join('\n'));
    } catch (err) { alert(err.message); }
    setUploading(false);
  };

  // #1211: Edit-Roundtrip — Download Original-DOCX
  const handleDownloadTemplate = (tmpl) => {
    const url = `${API}/api/vertraege/templates/${tmpl.id}/download`;
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.click();
  };

  // #1211: Edit-Roundtrip — Re-Upload bearbeitete DOCX (ersetzt template_pfad)
  const handleReplaceTemplate = (tmpl) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.docx';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.name.toLowerCase().endsWith('.docx')) { alert('Nur DOCX-Dateien (.docx)'); return; }
      try {
        const fd = new FormData();
        fd.append('template', file);
        const res = await fetch(`${API}/api/vertraege/templates/${tmpl.id}/datei`, {
          method: 'PUT', credentials: 'include', body: fd,
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Fehler');
        const data = await res.json();
        fetchDigital();
        const parts = [`✓ Vorlage "${tmpl.name}" ersetzt.`];
        if (data.erkannte_variablen?.length > 0) parts.push(`${t('vertrag.erkannteVariablen')}: ${data.erkannte_variablen.join(', ')}`);
        if (data.erkannte_anchors?.length > 0) parts.push(`Signatur-Platzhalter: ${data.erkannte_anchors.join(', ')}`);
        if (data.warnings?.length > 0) parts.push('\n⚠ ' + data.warnings.map(w => w.message).join('\n⚠ '));
        alert(parts.join('\n'));
      } catch (err) { alert(err.message); }
    };
    input.click();
  };

  const fetchDgSignature = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/vertraege/dg-signature`, { credentials: 'include' });
      if (res.ok) setDgSignature(await res.json());
    } catch { /* */ }
  }, []);

  useEffect(() => { fetchDgSignature(); }, [fetchDgSignature]);

  const handleDgUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.png')) { alert('Nur PNG-Dateien'); return; }
    setDgUploading(true);
    try {
      const fd = new FormData();
      fd.append('signature', file);
      const res = await fetch(`${API}/api/vertraege/dg-signature`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Fehler');
      setDgCacheBust(Date.now());
      await fetchDgSignature();
    } catch (err) { alert(err.message); }
    setDgUploading(false);
  };

  const handleDgDelete = async () => {
    if (!confirm('DG-Unterschrift löschen?')) return;
    await fetch(`${API}/api/vertraege/dg-signature`, { method: 'DELETE', credentials: 'include' });
    setDgCacheBust(Date.now());
    await fetchDgSignature();
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/vertrag/unterschreiben/${token}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for non-HTTPS contexts
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.cssText = 'position:fixed;left:-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setToast(t('vertrag.linkKopiert') || 'Link kopiert');
    setTimeout(() => setToast(''), 2500);
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('de-AT') : '–';

  // KPIs
  const dvAktiv = dvList.filter(d => d.ist_aktiv).length;
  const dvAuslaufend = dvList.filter(d => {
    if (!d.end_datum) return false;
    const diff = (new Date(d.end_datum) - new Date()) / (1000 * 60 * 60 * 24);
    return diff > 0 && diff <= 30;
  }).length;
  const kpiEntwurf = vertraege.filter(v => v.status === 'entwurf').length;
  const kpiGesendet = vertraege.filter(v => v.status === 'gesendet').length;
  const kpiUnterschrieben = vertraege.filter(v => v.status === 'unterschrieben').length;

  const tabItems = [
    { key: 'dienstvertraege', label: t('dienstvertrag.tabDV') },
    { key: 'digital', label: t('dienstvertrag.tabDigital') },
    { key: 'templates', label: t('vertrag.templates') },
  ];

  const fieldLabel = (key) => {
    const std = standardVars.find(s => s.key === key);
    return std?.label || t(`vertrag.var.${key}`) || key;
  };

  // #1810 F3: harte Sperre statt uebergehbarem Hinweis (Backend spiegelt das mit POSITION_FEHLT).
  const positionFehlt = (vollstaendigkeit?.fehlend || [])
    .some(e => (typeof e === 'string' ? e : e?.key) === 'position');

  return (
    <div className="px-4 sm:px-6 py-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary,#1A1A2E)]">{t('dienstvertrag.title')}</h1>
          <p className="text-sm text-[var(--text-tertiary,#6B7280)]">{t('dienstvertrag.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          {tab === 'dienstvertraege' && (
            <button onClick={openDvCreate} className="px-4 py-2 bg-[#338edc] text-white rounded-lg text-sm font-medium hover:bg-[#2a7bc8]">
              + {t('dienstvertrag.newDV')}
            </button>
          )}
          {tab === 'digital' && (
            <button onClick={openCreate} className="px-4 py-2 bg-[#338edc] text-white rounded-lg text-sm font-medium hover:bg-[#2a7bc8]">
              + {t('vertrag.newVertrag')}
            </button>
          )}
          {tab === 'templates' && (
            <button onClick={() => { setUploadForm({ name: '', typ: 'vollzeit' }); setUploadFile(null); setShowUpload(true); }} className="px-4 py-2 bg-[#338edc] text-white rounded-lg text-sm font-medium hover:bg-[#2a7bc8]">
              + {t('vertrag.newTemplate')}
            </button>
          )}
        </div>
      </div>

      {/* KPI bar */}
      {tab === 'dienstvertraege' ? (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: t('dienstvertrag.kpiAktiv'), value: dvAktiv, icon: FileCheck, color: 'text-[#10B981]' },
            { label: t('dienstvertrag.kpiAuslaufend'), value: dvAuslaufend, icon: FileClock, color: 'text-[#F59E0B]' },
            { label: t('dienstvertrag.kpiGesamt'), value: dvList.length, icon: Files, color: 'text-[#338edc]' },
          ].map(k => (
            <div key={k.label} className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 bg-[var(--bg-card,#fff)]">
              <div className="flex items-center gap-2 mb-1">
                <k.icon size={16} className={k.color} />
                <p className="text-xs text-[var(--text-tertiary,#6B7280)]">{k.label}</p>
              </div>
              <p className="text-2xl font-bold mt-1 text-[var(--text-primary,#1A1A2E)]">{k.value}</p>
            </div>
          ))}
        </div>
      ) : tab === 'digital' ? (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: t('vertrag.entwurf'), value: kpiEntwurf, icon: FileText, color: 'text-[#6B7280]' },
            { label: t('vertrag.gesendet'), value: kpiGesendet, icon: FileClock, color: 'text-[#338edc]' },
            { label: t('vertrag.unterschrieben'), value: kpiUnterschrieben, icon: FileCheck, color: 'text-[#10B981]' },
          ].map(k => (
            <div key={k.label} className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 bg-[var(--bg-card,#fff)]">
              <div className="flex items-center gap-2 mb-1">
                <k.icon size={16} className={k.color} />
                <p className="text-xs text-[var(--text-tertiary,#6B7280)]">{k.label}</p>
              </div>
              <p className="text-2xl font-bold mt-1 text-[var(--text-primary,#1A1A2E)]">{k.value}</p>
            </div>
          ))}
        </div>
      ) : null}

      {/* Tab bar */}
      <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex gap-1">
        {tabItems.map(tb => (
          <button key={tb.key} onClick={() => setTab(tb.key)}
            className={`px-4 py-1.5 rounded-[8px] text-sm font-medium transition-all ${tab === tb.key ? 'bg-white shadow-sm text-[var(--text-primary,#1A1A2E)] font-semibold dark:bg-[var(--bg-card)]' : 'text-[var(--text-secondary,#4B5563)] hover:text-[var(--text-primary,#1A1A2E)]'}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {/* ═══ TAB: Dienstverträge ═══ */}
      {tab === 'dienstvertraege' && (
        <>
          <div className="flex gap-3 flex-wrap">
            <select value={dvFilter.ma} onChange={e => setDvFilter(f => ({ ...f, ma: e.target.value }))}
              className="border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]">
              <option value="">{t('dienstvertrag.allMA')}</option>
              {mitarbeiterList.map(m => <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>)}
            </select>
            <select value={dvFilter.aktiv} onChange={e => setDvFilter(f => ({ ...f, aktiv: e.target.value }))}
              className="border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]">
              <option value="">{t('dienstvertrag.allStatus')}</option>
              <option value="true">{t('dienstvertrag.onlyActive')}</option>
            </select>
          </div>

          <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden bg-[var(--bg-card,#fff)]">
            {dvLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" />
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('dienstvertrag.colMA')}</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('dienstvertrag.colBezeichnung')}</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('dienstvertrag.colTyp')}</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('dienstvertrag.colLaufzeit')}</th>
                    <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('dienstvertrag.colBrutto')}</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('common.status')}</th>
                    <th className="text-center px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">PDF</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
                  {dvList.map(dv => {
                    const name = dv.vorname ? `${dv.vorname} ${dv.nachname}` : `MA #${dv.mitarbeiter_id}`;
                    const dvSt = DV_STATUS[dv.status] || 'bg-gray-100 text-gray-600';
                    const typLabel = TYP_LABELS[dv.vertrag_typ] || dv.vertrag_typ || '–';
                    const hasPdf = !!dv.pdf_pfad;
                    return (
                      <tr key={dv.id} className="hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
                        <td className="px-4 py-3 font-medium text-[var(--text-primary,#1A1A2E)]">{name}</td>
                        <td className="px-4 py-3 text-[var(--text-secondary,#4B5563)]">{dv.bezeichnung || '–'}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700">{typLabel}</span>
                        </td>
                        <td className="px-4 py-3 text-[var(--text-tertiary,#6B7280)]">
                          {fmtDate(dv.start_datum)} – {dv.end_datum ? fmtDate(dv.end_datum) : t('dienstvertrag.unbefristet')}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-[var(--text-primary,#1A1A2E)]">
                          {dv.brutto_monatlich ? `€ ${Number(dv.brutto_monatlich).toLocaleString('de-DE')}` : '–'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${dvSt}`}>{dv.status || '–'}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {hasPdf ? (
                            <a href={`${API}/api/dienstvertraege/${dv.id}/pdf`} target="_blank" rel="noreferrer"
                              className="px-2 py-1 border border-[var(--border-default,#E5E7EB)] rounded-lg text-xs hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)]">PDF</a>
                          ) : (
                            <span className="text-xs text-[var(--text-muted,#9CA3AF)]">–</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {dvList.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--text-muted,#9CA3AF)]">{t('dienstvertrag.keineDV')}</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ═══ TAB: Digitale Verträge ═══ */}
      {tab === 'digital' && (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden bg-[var(--bg-card,#fff)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('vertrag.person')}</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('vertrag.template')}</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('vertrag.status')}</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('vertrag.datum')}</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('vertrag.aktionen')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
              {vertraege.map(v => {
                const name = v.ma_vorname ? `${v.ma_vorname} ${v.ma_nachname}` : v.bew_vorname ? `${v.bew_vorname} ${v.bew_nachname}` : '—';
                const st = STATUS_MAP[v.status] || STATUS_MAP.entwurf;
                return (
                  <tr key={v.id} className="hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
                    <td className="px-4 py-3 font-medium text-[var(--text-primary,#1A1A2E)]">{name}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary,#4B5563)]">{v.template_name}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${st.bg} ${st.text}`}>
                        {v.status === 'entwurf' ? t('vertrag.entwurf')
                          : v.status === 'gesendet' ? t('vertrag.gesendet')
                          : v.status === 'unterschrieben' ? t('vertrag.unterschrieben')
                          : v.status === 'resend_pending' ? (t('vertrag.statusResendPending') || st.label)
                          : v.status === 'storniert' ? (t('vertrag.statusStorniert') || st.label)
                          : st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[var(--text-tertiary,#6B7280)]">{fmtDate(v.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5 relative">
                        {/* #1205-F: Auge-Icon → Re-render PDF aus DB-Werten (Modal mit Iframe) */}
                        <button
                          onClick={() => setPreviewModal({
                            mode: 'get',
                            endpoint: `/api/vertraege/${v.id}/preview`,
                            title: `${t('vertrag.vorschau') || 'Vorschau'}: ${name}`,
                            downloadFileName: `vertrag-${v.id}-preview.pdf`,
                          })}
                          title={t('vertrag.vorschau') || 'Vorschau'}
                          className="p-1.5 border border-[var(--border-default,#E5E7EB)] rounded-lg text-[var(--text-tertiary,#6B7280)] hover:text-[#338edc] hover:bg-[var(--bg-inset,#F8F9FC)]"
                        >
                          <Eye size={14} />
                        </button>
                        <a href={`${API}/api/vertraege/${v.id}/pdf`} target="_blank" rel="noreferrer"
                          className="px-2.5 py-1 border border-[var(--border-default,#E5E7EB)] rounded-lg text-xs hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)]">PDF</a>

                        {/* Nicht-admin Quick-Actions bleiben inline, damit ops_manager weiter senden/Link kopieren kann. */}
                        {v.status === 'entwurf' && (
                          <>
                            <button onClick={() => handleSend(v.id, 'email')} className="px-2.5 py-1 bg-[#338edc] text-white rounded-lg text-xs hover:bg-[#2a7bc8]">{t('vertrag.senden')}</button>
                            <button onClick={() => copyLink(v.unterschrift_token)} className="px-2.5 py-1 border border-[var(--border-default,#E5E7EB)] rounded-lg text-xs hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)]">Link</button>
                          </>
                        )}
                        {(v.status === 'gesendet' || v.status === 'resend_pending') && (
                          <button onClick={() => copyLink(v.unterschrift_token)} className="px-2.5 py-1 border border-[var(--border-default,#E5E7EB)] rounded-lg text-xs hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)]">Link</button>
                        )}

                        {/* Admin-Dropdown: status-abhängige destruktive Aktionen (#887).
                            Button bleibt in der Zelle; das Menü rendert via Portal in document.body,
                            damit es vom `overflow-hidden`-Table-Wrapper nicht geclippt wird. */}
                        {isAdmin && (
                          <button
                            ref={el => { if (el) menuBtnRefs.current[v.id] = el; }}
                            onClick={(e) => openAdminMenu(v.id, e.currentTarget)}
                            className="p-1.5 border border-[var(--border-default,#E5E7EB)] rounded-lg text-[var(--text-tertiary,#6B7280)] hover:bg-[var(--bg-inset,#F8F9FC)]"
                            title={t('vertrag.adminAktionen') || 'Admin-Aktionen'}
                            aria-label={t('vertrag.adminAktionen') || 'Admin-Aktionen'}
                          >
                            <MoreHorizontal size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {vertraege.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[var(--text-muted,#9CA3AF)]">{t('vertrag.keineVertraege')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ TAB: Templates ═══ */}
      {tab === 'templates' && (
        <>
          <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden bg-[var(--bg-card,#fff)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('vertrag.templateName')}</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('vertrag.typ')}</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('vertrag.format')}</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('vertrag.variablen')}</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('vertrag.status')}</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-[var(--text-secondary,#4B5563)]">{t('vertrag.aktionen')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
                {templates.map(tmpl => (
                  <tr key={tmpl.id} className="hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
                    <td className="px-4 py-3 font-medium text-[var(--text-primary,#1A1A2E)]">{tmpl.name}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary,#4B5563)]">{TYP_LABELS[tmpl.typ] || tmpl.typ}</td>
                    <td className="px-4 py-3">
                      {tmpl.format === 'docx' ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">DOCX</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700" title={t('vertrag.legacyHint')}>
                          <AlertTriangle size={11} /> {t('vertrag.legacyBadge')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[var(--text-tertiary,#6B7280)]">
                      {Array.isArray(tmpl.variablen) && tmpl.variablen.length > 0
                        ? tmpl.variablen.map(v => v.key || v).join(', ')
                        : <span className="text-[var(--text-muted,#9CA3AF)]">–</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${tmpl.aktiv ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {tmpl.aktiv ? t('vertrag.aktiv') : t('vertrag.inaktiv')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* #1205-F: Auge-Icon → Template-Preview mit Beispiel-Daten */}
                        {tmpl.format === 'docx' && (
                          <button onClick={() => setPreviewModal({
                            mode: 'get',
                            endpoint: `/api/vertraege/templates/${tmpl.id}/preview`,
                            title: `${t('vertrag.vorschau') || 'Vorschau'}: ${tmpl.name}`,
                            downloadFileName: `${tmpl.name}-preview.pdf`,
                          })}
                            title={t('vertrag.vorschau') || 'Vorschau'}
                            className="p-1.5 text-[var(--text-tertiary,#6B7280)] hover:text-[#338edc] transition-colors">
                            <Eye size={16} />
                          </button>
                        )}
                        {/* #1211: Download Original-DOCX (Edit-Roundtrip) */}
                        <button onClick={() => handleDownloadTemplate(tmpl)}
                          title="Original-DOCX herunterladen (zum Editieren)"
                          className="p-1.5 text-[var(--text-tertiary,#6B7280)] hover:text-[#338edc] transition-colors">
                          <Download size={16} />
                        </button>
                        {/* #1211: Re-Upload bearbeitete DOCX */}
                        {tmpl.format === 'docx' && (
                          <button onClick={() => handleReplaceTemplate(tmpl)}
                            title="Bearbeitete DOCX hochladen (ersetzt Datei)"
                            className="p-1.5 text-[var(--text-tertiary,#6B7280)] hover:text-[#338edc] transition-colors">
                            <Upload size={16} />
                          </button>
                        )}
                        <button onClick={() => handleToggleTemplate(tmpl.id, !tmpl.aktiv)}
                          title={tmpl.aktiv ? t('vertrag.deaktivieren') : t('vertrag.aktivieren')}
                          className="p-1.5 text-[var(--text-tertiary,#6B7280)] hover:text-amber-500 transition-colors">
                          {tmpl.aktiv ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                        <button onClick={() => handleDeleteTemplate(tmpl)}
                          title={t('vertrag.loeschen')}
                          className="p-1.5 text-[var(--text-tertiary,#6B7280)] hover:text-red-500 transition-colors">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {templates.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--text-muted,#9CA3AF)]">{t('vertrag.keineTemplates')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {standardVars.length > 0 && (
            <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 bg-[var(--bg-card,#fff)] mt-4">
              <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-2">{t('vertrag.standardVariablen')}</h3>
              <p className="text-xs text-[var(--text-tertiary,#6B7280)] mb-3">{t('vertrag.variablenHint')}</p>
              <div className="flex flex-wrap gap-2">
                {standardVars.map(v => (
                  <span key={v.key} className="text-xs bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] px-2 py-1 rounded text-[var(--text-secondary,#4B5563)]">
                    {`{{${v.key}}}`} <span className="text-[var(--text-muted,#9CA3AF)]">= {v.label}</span>
                  </span>
                ))}
              </div>

              <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-2 mt-5">Signatur-Platzhalter</h3>
              <p className="text-xs text-[var(--text-tertiary,#6B7280)] mb-3">
                Einfache Klammern — werden nach DOCX→PDF-Konversion durch Unterschriften/Datum ersetzt.
                {' '}<span className="text-[var(--text-secondary,#4B5563)] font-medium">{'{unterschrift_mitarbeiter}'}</span> muss pro Vorlage einmal enthalten sein.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] px-2 py-1 rounded text-[var(--text-secondary,#4B5563)]">
                  {'{unterschrift_mitarbeiter}'} <span className="text-[var(--text-muted,#9CA3AF)]">= MA-Unterschrift (Pflicht)</span>
                </span>
                <span className="text-xs bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] px-2 py-1 rounded text-[var(--text-secondary,#4B5563)]">
                  {'{unterschrift_dienstgeber}'} <span className="text-[var(--text-muted,#9CA3AF)]">= DG-Unterschrift (optional)</span>
                </span>
                <span className="text-xs bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] px-2 py-1 rounded text-[var(--text-secondary,#4B5563)]">
                  {'{unterschrift_datum}'} <span className="text-[var(--text-muted,#9CA3AF)]">= Unterschriftsdatum TT.MM.JJJJ (optional)</span>
                </span>
              </div>
            </div>
          )}

          <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 bg-[var(--bg-card,#fff)] mt-4">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">Dienstgeber-Unterschrift</h3>
                <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-1">
                  PNG-Bild wird in <span className="font-mono">{'{unterschrift_dienstgeber}'}</span>-Platzhalter gezeichnet.
                  Transparenter Hintergrund empfohlen. Max. 2 MB.
                </p>
              </div>
              {dgSignature.vorhanden && (
                <button onClick={handleDgDelete} className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1">
                  <Trash2 size={12} /> Löschen
                </button>
              )}
            </div>
            {dgSignature.vorhanden ? (
              <div className="flex items-center gap-4">
                <img src={`${API}/api/vertraege/dg-signature/bild?v=${dgCacheBust}`}
                  alt="DG Unterschrift"
                  className="h-20 border border-[var(--border-default,#E5E7EB)] rounded bg-white p-2" />
                <div className="text-xs text-[var(--text-tertiary,#6B7280)]">
                  <div>Hochgeladen: {dgSignature.geaendert_am ? new Date(dgSignature.geaendert_am).toLocaleString('de-AT') : '–'}</div>
                  <div>Größe: {dgSignature.groesse ? `${Math.round(dgSignature.groesse / 1024)} KB` : '–'}</div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-amber-600 mb-3">⚠ Keine DG-Unterschrift hochgeladen. Platzhalter bleibt leer beim Signieren.</p>
            )}
            <label className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 text-xs font-medium border border-[var(--border-default,#E5E7EB)] rounded-lg bg-[var(--bg-inset,#F8F9FC)] hover:bg-[var(--bg-card-hover,#FAFAFE)] cursor-pointer">
              <Download size={12} className="rotate-180" />
              {dgUploading ? '...' : (dgSignature.vorhanden ? 'Ersetzen (PNG)' : 'PNG hochladen')}
              <input type="file" accept=".png,image/png" className="hidden"
                onChange={e => { handleDgUpload(e.target.files[0]); e.target.value = ''; }} />
            </label>
          </div>
        </>
      )}

      {/* ═══ DV Create Modal ═══ */}
      {showDvCreate && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowDvCreate(false)}>
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
              <h2 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">{t('dienstvertrag.newDV')}</h2>
              <button onClick={() => setShowDvCreate(false)} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary,#1A1A2E)]"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              {activeDvWarning && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{t('dienstvertrag.warnActiveExists', { bezeichnung: activeDvWarning.bezeichnung, datum: dvForm.start_datum || '...' })}</span>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('dienstvertrag.colMA')} *</label>
                <select value={dvForm.mitarbeiter_id} onChange={e => { setDvForm(p => ({ ...p, mitarbeiter_id: e.target.value, __override_grund: '' })); checkActiveDv(e.target.value); }}
                  className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]">
                  <option value="">—</option>
                  {mitarbeiterList.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.vorname} {m.nachname}{m.austritt_datum ? ` (${t('dienstvertrag.ausgetretenAm') || 'ausgetreten'} ${m.austritt_datum})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              {getSelectedMaAustritt(dvForm.mitarbeiter_id) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-3 space-y-2">
                  <div className="flex items-start gap-2 text-xs text-amber-800">
                    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                    <span>
                      {t('dienstvertrag.austrittWarn') || 'Mitarbeiter ist ausgetreten'} ({getSelectedMaAustritt(dvForm.mitarbeiter_id)}).
                      {' '}{t('dienstvertrag.austrittHinweis') || 'Nachträglicher Vertrag erfordert Admin-Override mit Begründung.'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-amber-900 mb-1">
                      {t('dienstvertrag.overrideReasonLabel') || 'Grund (Pflicht, mind. 10 Zeichen)'} *
                    </label>
                    <textarea
                      value={dvForm.__override_grund}
                      onChange={e => setDvForm(p => ({ ...p, __override_grund: e.target.value }))}
                      maxLength={1000}
                      rows={3}
                      placeholder={t('dienstvertrag.overrideReasonPlaceholder') || 'z.B. Vertrag wurde unterschrieben aber nicht digitalisiert; nachträgliche Erfassung für Lohnnachverrechnung'}
                      className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white text-[var(--text-primary,#1A1A2E)]"
                    />
                    <div className="text-[11px] text-amber-700 mt-1">{(dvForm.__override_grund || '').length} / 1000</div>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('dienstvertrag.colBezeichnung')} *</label>
                <input value={dvForm.bezeichnung} onChange={e => setDvForm(p => ({ ...p, bezeichnung: e.target.value }))}
                  className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" placeholder={t('dienstvertrag.placeholderBezeichnung')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('dienstvertrag.colTyp')}</label>
                <select value={dvForm.vertrag_typ} onChange={e => setDvForm(p => ({ ...p, vertrag_typ: e.target.value }))}
                  className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]">
                  {Object.entries(TYP_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('dienstvertrag.gueltigAb')} *</label>
                  <input type="date" value={dvForm.start_datum} onChange={e => setDvForm(p => ({ ...p, start_datum: e.target.value }))}
                    className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('dienstvertrag.gueltigBis')}</label>
                  <input type="date" value={dvForm.end_datum} onChange={e => setDvForm(p => ({ ...p, end_datum: e.target.value }))}
                    className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('dienstvertrag.colBrutto')}</label>
                  <input type="number" value={dvForm.brutto_monatlich} onChange={e => setDvForm(p => ({ ...p, brutto_monatlich: e.target.value }))}
                    className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" placeholder="€" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('dienstvertrag.wochenstunden')}</label>
                  <input type="number" value={dvForm.stunden_pro_woche} onChange={e => setDvForm(p => ({ ...p, stunden_pro_woche: e.target.value }))}
                    className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" placeholder="h" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('dienstvertrag.position')}</label>
                <input value={dvForm.position} onChange={e => setDvForm(p => ({ ...p, position: e.target.value }))}
                  className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" />
              </div>
              <label className="flex items-start gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={dvForm.ist_zusatzvereinbarung} onChange={e => setDvForm(p => ({ ...p, ist_zusatzvereinbarung: e.target.checked }))} className="mt-0.5" />
                <span className="text-[var(--text-secondary,#4B5563)]">{t('vertrag.fieldZusatz', 'Zusatzvereinbarung / Änderung (Brutto, Stunden & Stelle nicht zwingend)')}</span>
              </label>
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">PDF</label>
                <input type="file" accept="application/pdf" onChange={e => setDvFile(e.target.files[0])} className="w-full text-sm text-[var(--text-primary,#1A1A2E)]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('vertrag.notiz')}</label>
                <textarea value={dvForm.notiz} onChange={e => setDvForm(p => ({ ...p, notiz: e.target.value }))}
                  className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" rows={2} />
              </div>
              {dvError && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-700 flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{dvError}</span>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
              <button onClick={() => setShowDvCreate(false)} className="px-4 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">{t('vertrag.abbrechen')}</button>
              <button onClick={handleDvCreate} disabled={dvCreating || !dvForm.mitarbeiter_id || !dvForm.bezeichnung || !dvForm.start_datum || (getSelectedMaAustritt(dvForm.mitarbeiter_id) && (dvForm.__override_grund || '').trim().length < 10)}
                className="px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                {dvCreating ? '...' : t('dienstvertrag.erstellen')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Digital Create Modal (Multi-Step Wizard, #1205 v2.519.0) ═══
           Step 0: Stammdaten-Vollständigkeits-Check (nur wenn unvollständig)
           Step 1: Template + Person wählen
           Step 2: Read-only Variablen-Vorschau aus Stammdaten + Pencil-Deep-Link */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
              <div className="flex items-center gap-3">
                {(createStep === 0 || createStep === 2) && (
                  <button onClick={() => setCreateStep(1)} className="p-1 text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary,#1A1A2E)]">
                    <ChevronLeft size={18} />
                  </button>
                )}
                <div>
                  <h2 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">{t('vertrag.newVertrag')}</h2>
                  <p className="text-xs text-[var(--text-muted,#9CA3AF)]">
                    {createStep === 0 ? (t('vertrag.step0') || 'Stammdaten-Check')
                      : createStep === 1 ? t('vertrag.step1')
                      : t('vertrag.step2')}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowCreate(false)} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary,#1A1A2E)]"><X size={18} /></button>
            </div>

            {/* Step 0: Vollständigkeits-Block (#1205-E) */}
            {createStep === 0 && vollstaendigkeit && (
              <>
                <div className="p-6 space-y-4">
                  {/* #1810 F3: Fehlende Berufsbezeichnung ist kein Hinweis, sondern ein Stopp —
                      sie IST die Position im Vertragstext, ein Rechtsdokument mit leerem Feld
                      darf nicht entstehen. Alle uebrigen Luecken bleiben uebergehbar wie bisher. */}
                  <div className={positionFehlt
                    ? 'bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-3'
                    : 'bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3'}>
                    <AlertTriangle size={18} className={positionFehlt ? 'text-red-500 mt-0.5 flex-shrink-0' : 'text-amber-500 mt-0.5 flex-shrink-0'} />
                    <div className="flex-1">
                      <p className={positionFehlt ? 'text-sm font-medium text-red-900' : 'text-sm font-medium text-amber-900'}>
                        {positionFehlt
                          ? (t('vertrag.positionFehlt') || 'Position/Berufsbezeichnung fehlt — Vertrag kann nicht erstellt werden')
                          : (t('vertrag.stammdatenUnvollstaendig') || 'Stammdaten unvollständig')}
                      </p>
                      <p className={positionFehlt ? 'text-xs text-red-800 mt-1' : 'text-xs text-amber-800 mt-1'}>
                        {positionFehlt
                          ? (t('vertrag.positionFehltHint') || 'Die Berufsbezeichnung aus den Stammdaten ist die Position im Vertragstext. Bitte zuerst ergänzen — die Anlage ist bis dahin gesperrt.')
                          : (t('vertrag.stammdatenUnvollstaendigHint') || 'Folgende Pflichtfelder fehlen für eine korrekte Vertragsgenerierung:')}
                      </p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {(vollstaendigkeit.fehlend || []).map((entry) => {
                      const label = typeof entry === 'string' ? entry : (entry.label || entry.key);
                      const key = typeof entry === 'string' ? entry : entry.key;
                      return (
                        <li key={key} className="flex items-center gap-2 text-sm text-[var(--text-secondary,#4B5563)]">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                          <span>{label}</span>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] rounded-xl p-4">
                    <p className="text-xs text-[var(--text-tertiary,#6B7280)] mb-2">
                      {t('vertrag.stammdatenDeepLinkHint') || 'Öffne die Mitarbeiter-Detailseite in einem neuen Tab, ergänze die Felder und klicke dann auf „Erneut prüfen".'}
                    </p>
                    <a
                      href={`/mitarbeiter/${createForm.mitarbeiter_id}?tab=stammdaten`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[10px] border border-[#338edc] bg-white text-sm font-medium text-[#338edc] hover:bg-[#338edc] hover:text-white transition-colors"
                    >
                      <ExternalLink size={14} /> {t('vertrag.stammdatenOeffnen') || 'Stammdaten öffnen'}
                    </a>
                  </div>
                </div>
                <div className="flex justify-between gap-2 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
                  <button onClick={() => setCreateStep(1)} className="px-4 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">{t('vertrag.zurueck')}</button>
                  <div className="flex gap-2">
                    <button onClick={reloadAfterStammdatenFix} disabled={loadingVars}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-[10px] border border-[#338edc] bg-white text-[#338edc] text-sm font-semibold disabled:opacity-50 hover:bg-[#338edc] hover:text-white transition-colors">
                      <RefreshCw size={14} /> {loadingVars ? '...' : (t('vertrag.erneutPruefen') || 'Erneut prüfen')}
                    </button>
                    {!positionFehlt && (
                      <button onClick={() => setCreateStep(2)}
                        className="px-4 py-2 rounded-[10px] bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition-colors">
                        {t('vertrag.trotzdemFortfahren') || 'Trotzdem fortfahren'}
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Step 1: Template + Person */}
            {createStep === 1 && (
              <>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('vertrag.template')} *</label>
                    <select value={createForm.template_id} onChange={e => setCreateForm(p => ({ ...p, template_id: e.target.value }))}
                      className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]">
                      <option value="">{t('vertrag.selectTemplate')}</option>
                      {templates.filter(tp => tp.aktiv).map(tp => (
                        <option key={tp.id} value={tp.id} disabled={tp.format !== 'docx'}>
                          {tp.name} ({TYP_LABELS[tp.typ] || tp.typ})
                          {tp.format === 'docx' ? ' · DOCX' : ` · ${t('vertrag.legacyBadge')}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('vertrag.mitarbeiter')}</label>
                    <select value={createForm.mitarbeiter_id} onChange={e => setCreateForm(p => ({ ...p, mitarbeiter_id: e.target.value, bewerber_id: '' }))}
                      className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]">
                      <option value="">—</option>
                      {mitarbeiterList.map(m => <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('vertrag.bewerber')}</label>
                    <select value={createForm.bewerber_id} onChange={e => setCreateForm(p => ({ ...p, bewerber_id: e.target.value, mitarbeiter_id: '' }))}
                      className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]">
                      <option value="">—</option>
                      {bewerberList.map(b => <option key={b.id} value={b.id}>{b.vorname} {b.nachname}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('vertrag.notiz')}</label>
                    <textarea value={createForm.notiz} onChange={e => setCreateForm(p => ({ ...p, notiz: e.target.value }))}
                      className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" rows={2} />
                  </div>
                </div>
                <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
                  <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">{t('vertrag.abbrechen')}</button>
                  <button onClick={goToStep2} disabled={loadingVars || !createForm.template_id || (!createForm.mitarbeiter_id && !createForm.bewerber_id)}
                    className="flex items-center gap-1 px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                    {loadingVars ? '...' : <>{t('vertrag.weiter')} <ChevronRight size={16} /></>}
                  </button>
                </div>
              </>
            )}

            {/* Step 2: Read-only Variablen-Vorschau (#1205-A,B,C)
                 Alle Werte read-only aus Stammdaten. Pencil-Icon öffnet MA-Detail im neuen Tab.
                 SVNR-Validierung entfernt — Geburtsdatum kommt aus mitarbeiter.birthday, nicht SVNR.
                 Urlaubstage als Read-only-Block mit Wartefrist-Hinweis (#1202). */}
            {createStep === 2 && (
              <>
                <div className="p-6 space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-3">
                    <FileText size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-blue-800">
                        {t('vertrag.readOnlyInfo') || 'Alle Felder kommen aus den Stammdaten. Änderungen bitte direkt im Mitarbeiter-Detail vornehmen.'}
                      </p>
                      {createForm.mitarbeiter_id && (
                        <a
                          href={`/mitarbeiter/${createForm.mitarbeiter_id}?tab=stammdaten`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900 mt-1.5"
                        >
                          <ExternalLink size={12} /> {t('vertrag.stammdatenOeffnen') || 'Stammdaten öffnen'}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Urlaubstage-Read-only-Block (#1205-B / #1202) */}
                  {createForm.mitarbeiter_id && urlaubsInfo && (
                    <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 bg-[var(--bg-inset,#F8F9FC)]">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">
                          {t('vertrag.urlaubsanspruch') || 'Urlaubsanspruch'}
                        </p>
                        <span className="text-[11px] font-mono text-[var(--text-muted,#9CA3AF)]">aus Arbeitszeitmodell</span>
                      </div>
                      {/* #1209 B: Großer Wert = Jahresanspruch (das landet im Vertrag).
                           „aufgebaut bisher" als kleine Sub-Info, da pre-Eintritt/Wartefrist sonst 0 zeigt. */}
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-2xl font-bold text-[var(--text-primary,#1A1A2E)]">
                          {urlaubsInfo.anspruch_jahr ?? variablenWerte.urlaubstage ?? '–'}
                        </span>
                        <span className="text-sm text-[var(--text-tertiary,#6B7280)]">
                          {t('vertrag.tageJahr') || 'Tage / Jahr'}
                        </span>
                        {urlaubsInfo.anspruch_jahr != null && urlaubsInfo.anspruch_jahr !== urlaubsInfo.anspruch_voll && (
                          <span className="text-xs text-[var(--text-muted,#9CA3AF)] ml-auto">
                            {t('vertrag.aliquot') || 'aliquot v.'} {urlaubsInfo.anspruch_voll} Tage Vollzeit
                          </span>
                        )}
                      </div>
                      {urlaubsInfo.aufgebaut_aktuell != null && !urlaubsInfo.pre_eintritt && (
                        <p className="text-xs text-[var(--text-muted,#9CA3AF)] mt-1">
                          {t('vertrag.bisherAufgebaut') || 'bisher aufgebaut'}: {urlaubsInfo.aufgebaut_aktuell} {t('vertrag.tage') || 'Tage'}
                        </p>
                      )}
                      {urlaubsInfo.in_wartefrist && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 mt-2 flex items-center gap-1.5">
                          <Clock size={12} />
                          {urlaubsInfo.wartefrist_bis
                            ? `Wartefrist bis ${fmtDate(urlaubsInfo.wartefrist_bis)} — danach voller Anspruch.`
                            : (t('vertrag.inWartefrist') || 'Mitarbeiter ist in der Wartefrist.')}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    {getTemplateVars().map(v => {
                      const key = v.key || v;
                      const label = v.label || fieldLabel(key);
                      const isDate = DATE_FIELDS.includes(key);
                      // datum_heute + standort bleiben editierbar (kein Stammdaten-Feld).
                      // Bei Bewerber-Modus (kein MA-Detail) alles editierbar lassen.
                      const isEditable = !createForm.mitarbeiter_id
                        || key === 'datum_heute'
                        || key === 'standort'
                        || key === 'notiz';
                      const val = variablenWerte[key] || '';
                      const isWide = key === 'adresse' || key === 'standort';

                      if (isEditable) {
                        return (
                          <div key={key} className={isWide ? 'col-span-2' : ''}>
                            <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{label}</label>
                            {isDate ? (
                              <input
                                type="date"
                                value={val}
                                onChange={e => setVariablenWerte(prev => ({ ...prev, [key]: e.target.value }))}
                                className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20"
                              />
                            ) : (
                              <input
                                type={key === 'brutto' || key === 'wochenstunden' || key === 'urlaubstage' ? 'number' : 'text'}
                                value={val}
                                onChange={e => setVariablenWerte(prev => ({ ...prev, [key]: e.target.value }))}
                                className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20"
                                placeholder={label}
                              />
                            )}
                          </div>
                        );
                      }
                      // Read-only-Feld aus Stammdaten — mit Pencil-Deep-Link.
                      const displayVal = isDate && val ? fmtDate(val) : val;
                      const isEmpty = !val;
                      // #1212: probezeit_bis ist abgeleitet (eintritt_datum + 1 Monat) — kein Pflicht-Stammdatenfeld.
                      // Bei leer → grau "—" statt rot "fehlt" (Anzeige-Konvention für optionale/abgeleitete Felder).
                      const isOptional = key === 'probezeit_bis';
                      const showAsMissing = isEmpty && !isOptional;
                      return (
                        <div key={key} className={isWide ? 'col-span-2' : ''}>
                          <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{label}</label>
                          <div className={`group flex items-center gap-2 border rounded-lg px-3 py-2 text-sm ${showAsMissing ? 'border-red-200 bg-red-50/40' : 'border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]'}`}>
                            <span className={`flex-1 truncate ${showAsMissing ? 'text-red-500 italic' : (isEmpty ? 'text-[var(--text-tertiary,#6B7280)]' : 'text-[var(--text-primary,#1A1A2E)]')}`}>
                              {showAsMissing ? (t('vertrag.fehlt') || '— fehlt —') : (isEmpty ? '—' : displayVal)}
                            </span>
                            <a
                              href={`/mitarbeiter/${createForm.mitarbeiter_id}?tab=stammdaten&focus=${key}`}
                              target="_blank"
                              rel="noreferrer"
                              title={t('vertrag.bearbeitenInStammdaten') || 'In Stammdaten bearbeiten'}
                              className="text-[var(--text-tertiary,#6B7280)] hover:text-[#338edc] flex-shrink-0"
                            >
                              <Pencil size={14} />
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex justify-between gap-2 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
                  <button onClick={() => setCreateStep(1)} className="px-4 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">{t('vertrag.zurueck')}</button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPreviewModal({
                        mode: 'post',
                        endpoint: '/api/vertraege/preview',
                        body: {
                          template_id: Number(createForm.template_id),
                          variablen_werte: variablenWerte,
                          mitarbeiter_id: createForm.mitarbeiter_id ? Number(createForm.mitarbeiter_id) : undefined,
                          bewerber_id: createForm.bewerber_id ? Number(createForm.bewerber_id) : undefined,
                        },
                        title: t('vertrag.previewBefore') || 'Vorschau vor Generierung',
                        downloadFileName: 'vertrag-preview.pdf',
                      })}
                      disabled={creating || !createForm.template_id}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-[10px] border border-[#338edc] bg-white text-[#338edc] text-sm font-semibold disabled:opacity-50 hover:bg-[#338edc] hover:text-white transition-colors"
                    >
                      <Eye size={16} /> {t('vertrag.vorschau') || 'Vorschau'}
                    </button>
                    <button onClick={handleGenerate} disabled={creating}
                      className="flex items-center gap-2 px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                      {creating ? '...' : <><Download size={16} /> {t('vertrag.generieren')}</>}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ═══ Upload Template Modal ═══ */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowUpload(false)}>
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
              <h2 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">{t('vertrag.newTemplate')}</h2>
              <button onClick={() => setShowUpload(false)} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary,#1A1A2E)]"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('vertrag.templateName')}</label>
                <input value={uploadForm.name} onChange={e => setUploadForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" placeholder={t('vertrag.templateNamePlaceholder')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('vertrag.typ')}</label>
                <select value={uploadForm.typ} onChange={e => setUploadForm(p => ({ ...p, typ: e.target.value }))}
                  className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]">
                  {Object.entries(TYP_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('vertrag.templateDatei')}</label>
                <input type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={e => setUploadFile(e.target.files[0])}
                  className="w-full text-sm text-[var(--text-primary,#1A1A2E)]" />
                <p className="text-xs text-[var(--text-muted,#9CA3AF)] mt-1">{t('vertrag.uploadHint')}</p>
                <a href={`${API}/api/vertraege/beispiel-vorlage`}
                  className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-[#338edc] hover:underline">
                  <Download size={12} /> {t('vertrag.beispielDownload')}
                </a>
              </div>

              <div className="border border-[var(--border-default,#E5E7EB)] rounded-lg p-3 bg-[var(--bg-inset,#F8F9FC)]">
                <p className="text-xs font-semibold text-[var(--text-secondary,#4B5563)] mb-2">{t('vertrag.standardVariablen')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {standardVars.map(v => (
                    <span key={v.key} className="text-xs bg-white border border-[var(--border-default,#E5E7EB)] px-1.5 py-0.5 rounded text-[var(--text-secondary,#4B5563)] font-mono">
                      {`{{${v.key}}}`}
                    </span>
                  ))}
                </div>
                <p className="text-[11px] text-[var(--text-muted,#9CA3AF)] mt-2">{t('vertrag.variablenHint')}</p>

                <p className="text-xs font-semibold text-[var(--text-secondary,#4B5563)] mt-3 mb-2">Signatur-Platzhalter (einfache Klammern)</p>
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-xs bg-white border border-[var(--border-default,#E5E7EB)] px-1.5 py-0.5 rounded text-[var(--text-secondary,#4B5563)] font-mono">{'{unterschrift_mitarbeiter}'}</span>
                  <span className="text-xs bg-white border border-[var(--border-default,#E5E7EB)] px-1.5 py-0.5 rounded text-[var(--text-secondary,#4B5563)] font-mono">{'{unterschrift_dienstgeber}'}</span>
                  <span className="text-xs bg-white border border-[var(--border-default,#E5E7EB)] px-1.5 py-0.5 rounded text-[var(--text-secondary,#4B5563)] font-mono">{'{unterschrift_datum}'}</span>
                </div>
                <p className="text-[11px] text-[var(--text-muted,#9CA3AF)] mt-2">
                  Werden nach Konversion durch Unterschriften/Datum ersetzt. {'{unterschrift_mitarbeiter}'} muss einmal enthalten sein.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
              <button onClick={() => setShowUpload(false)} className="px-4 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">{t('vertrag.abbrechen')}</button>
              <button onClick={handleUploadTemplate} disabled={uploading || !uploadForm.name || !uploadFile}
                className="px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                {uploading ? '...' : t('vertrag.hochladen')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Confirm Dialog (#887: mit Grund + Tipp-Bestätigung für Admin-Aktionen) ═══ */}
      {confirmAction && (() => {
        const isAdminAction = isAdminActionType(confirmAction.type);
        const phrase = getConfirmPhrase(confirmAction.type);
        const isDestructive = ['deleteVertrag', 'stornieren', 'unsign', 'unsignAndResend', 'deleteTemplate'].includes(confirmAction.type);
        const title = isAdminAction
          ? (t(`vertrag.confirmTitle_${confirmAction.type}`) || t('vertrag.confirm'))
          : t('vertrag.confirm');
        const warning = isAdminAction
          ? (t(`vertrag.confirmWarn_${confirmAction.type}`) || confirmAction.message)
          : confirmAction.message;
        return (
          <div className="fixed inset-0 bg-black/30 z-[60] flex items-center justify-center p-4" onClick={() => { if (!actionBusy) setConfirmAction(null); }}>
            <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isDestructive ? 'bg-red-50' : 'bg-amber-50'}`}>
                    <AlertTriangle size={20} className={isDestructive ? 'text-red-500' : 'text-amber-500'} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)] mb-1">{title}</h3>
                    {confirmAction.name && (
                      <p className="text-xs text-[var(--text-tertiary,#6B7280)]">{confirmAction.name}</p>
                    )}
                  </div>
                </div>
                <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-4">{warning}</p>

                {isAdminAction && (
                  <>
                    <label className="block text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1">
                      {t('vertrag.grundLabel') || 'Grund'} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={grundInput}
                      onChange={e => setGrundInput(e.target.value)}
                      placeholder={t('vertrag.grundPlaceholder') || 'Warum wird diese Aktion ausgeführt? (geht ins Audit-Log)'}
                      rows={3}
                      className="w-full border border-[var(--border-default,#E5E7EB)] rounded-[10px] px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:ring-2 focus:ring-[#338edc] mb-3"
                    />
                    {phrase && (
                      <>
                        <label className="block text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1">
                          {(t('vertrag.phraseLabel') || 'Zur Bestätigung tippen:')} <code className="text-red-500 font-mono">{phrase}</code>
                        </label>
                        <input
                          value={confirmPhrase}
                          onChange={e => setConfirmPhrase(e.target.value)}
                          placeholder={phrase}
                          className="w-full border border-[var(--border-default,#E5E7EB)] rounded-[10px] px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] font-mono focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
                <button onClick={() => { if (!actionBusy) setConfirmAction(null); }} disabled={actionBusy}
                  className="px-4 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-card-hover,#FAFAFE)] disabled:opacity-50">
                  {t('vertrag.abbrechen')}
                </button>
                <button onClick={executeConfirm} disabled={actionBusy}
                  className={`px-4 py-2 rounded-[10px] text-white text-sm font-semibold transition-colors disabled:opacity-50 ${isDestructive ? 'bg-red-500 hover:bg-red-600' : 'bg-[#338edc] hover:bg-[#2b7bc4]'}`}>
                  {actionBusy ? '…' : (isDestructive ? t('vertrag.bestaetigenDestructive') || t('vertrag.bestaetigen') : t('vertrag.bestaetigen'))}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ Audit-Log Slideover (#887) ═══ */}
      {auditVertrag && (
        <div className="fixed inset-0 bg-black/30 z-[65] flex justify-end" onClick={() => setAuditVertrag(null)}>
          <div className="bg-[var(--bg-card,#fff)] h-full w-full max-w-md border-l border-[var(--border-default,#E5E7EB)] shadow-xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-[var(--border-default,#E5E7EB)] flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)] flex items-center gap-2">
                  <History size={16} /> {t('vertrag.auditLogTitle') || 'Audit-Log'}
                </h3>
                <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-0.5">
                  {auditVertrag.ma_vorname ? `${auditVertrag.ma_vorname} ${auditVertrag.ma_nachname}` : auditVertrag.bew_vorname ? `${auditVertrag.bew_vorname} ${auditVertrag.bew_nachname}` : `#${auditVertrag.id}`}
                  {' · '}{auditVertrag.template_name}
                </p>
              </div>
              <button onClick={() => setAuditVertrag(null)} className="p-1.5 hover:bg-[var(--bg-inset,#F8F9FC)] rounded-lg text-[var(--text-tertiary,#6B7280)]">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {auditLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" />
                </div>
              ) : auditEntries.length === 0 ? (
                <p className="text-sm text-[var(--text-muted,#9CA3AF)] text-center py-8">
                  {t('vertrag.auditEmpty') || 'Keine Audit-Einträge vorhanden.'}
                </p>
              ) : (
                <ol className="relative border-l border-[var(--border-default,#E5E7EB)] ml-3 space-y-4">
                  {auditEntries.map(e => {
                    const labels = {
                      created: { label: t('vertrag.auditCreated') || 'Erstellt', icon: FileText, color: 'bg-gray-100 text-gray-700' },
                      sent: { label: t('vertrag.auditSent') || 'Gesendet', icon: Send, color: 'bg-blue-50 text-blue-700' },
                      signed: { label: t('vertrag.auditSigned') || 'Unterschrieben', icon: CheckCircle, color: 'bg-green-50 text-green-700' },
                      unsigned: { label: t('vertrag.auditUnsigned') || 'Unterschrift gelöscht', icon: FileX, color: 'bg-red-50 text-red-700' },
                      resent: { label: t('vertrag.auditResent') || 'Erneut gesendet', icon: RefreshCw, color: 'bg-blue-50 text-blue-700' },
                      cancelled: { label: t('vertrag.auditCancelled') || 'Storniert', icon: Ban, color: 'bg-amber-50 text-amber-700' },
                      deleted: { label: t('vertrag.auditDeleted') || 'Gelöscht', icon: Trash2, color: 'bg-red-50 text-red-700' },
                    };
                    const meta = labels[e.aktion] || { label: e.aktion, icon: Clock, color: 'bg-gray-100 text-gray-700' };
                    const Icon = meta.icon;
                    const userName = e.user_vorname ? `${e.user_vorname} ${e.user_nachname || ''}`.trim() : (e.user_email || (e.ausgefuehrt_von ? `User #${e.ausgefuehrt_von}` : (t('vertrag.auditSystem') || 'System/Public')));
                    return (
                      <li key={e.id} className="ml-4">
                        <span className={`absolute -left-2.5 w-5 h-5 rounded-full flex items-center justify-center ${meta.color}`}>
                          <Icon size={10} />
                        </span>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${meta.color}`}>{meta.label}</span>
                          <span className="text-xs text-[var(--text-tertiary,#6B7280)]">
                            {new Date(e.ausgefuehrt_at).toLocaleString('de-AT', { timeZone: 'Europe/Vienna' })}
                          </span>
                        </div>
                        <p className="text-xs text-[var(--text-secondary,#4B5563)]">
                          <span className="font-medium">{userName}</span>
                        </p>
                        {e.grund && (
                          <p className="text-sm text-[var(--text-primary,#1A1A2E)] mt-1 bg-[var(--bg-inset,#F8F9FC)] rounded-lg px-3 py-2 border border-[var(--border-default,#E5E7EB)]">
                            „{e.grund}"
                          </p>
                        )}
                        {e.metadata && Object.keys(e.metadata).length > 0 && (
                          <details className="mt-1">
                            <summary className="text-xs text-[var(--text-muted,#9CA3AF)] cursor-pointer hover:text-[var(--text-secondary,#4B5563)]">
                              {t('vertrag.auditMetadata') || 'Details'}
                            </summary>
                            <pre className="text-[10px] text-[var(--text-tertiary,#6B7280)] mt-1 bg-[var(--bg-inset,#F8F9FC)] rounded-lg px-2 py-1 overflow-x-auto">
{JSON.stringify(e.metadata, null, 2)}
                            </pre>
                          </details>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ═══ Admin-Aktionen Dropdown — via Portal (#887 / v2.378.2) ═══
           Renders in document.body, damit der Table-Wrapper (overflow-hidden)
           das Menü nicht clippt. Position: fixed, auto-flip bei zu wenig Platz. */}
      {openMenuId !== null && (() => {
        const v = vertraege.find(x => x.id === openMenuId);
        if (!v) return null;
        const onAction = (type) => () => openAdminAction(type, v);
        const onAudit = () => openAuditLog(v);
        const itemClass = 'w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-[var(--bg-inset,#F8F9FC)]';
        const danger = 'text-red-600 hover:text-red-700';
        const normal = 'text-[var(--text-secondary,#4B5563)]';
        const menu = (
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              transform: menuPos.placement === 'top' ? 'translateY(-100%)' : 'none',
              zIndex: 9999,
              minWidth: 240,
            }}
            className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-xl shadow-sm py-1"
          >
            {v.status === 'entwurf' && (
              <button onClick={onAction('deleteVertrag')} className={`${itemClass} ${danger}`}>
                <Trash2 size={14} /> {t('vertrag.actionDelete') || 'Vertrag löschen'}
              </button>
            )}
            {(v.status === 'gesendet' || v.status === 'resend_pending') && (
              <button onClick={onAction('resend')} className={`${itemClass} ${normal}`}>
                <RefreshCw size={14} /> {t('vertrag.actionResend') || 'Link erneut senden'}
              </button>
            )}
            {v.status === 'unterschrieben' && (
              <>
                <button onClick={onAction('unsignAndResend')} className={`${itemClass} ${danger}`}>
                  <RefreshCw size={14} /> {t('vertrag.actionUnsignAndResend') || 'Unterschrift löschen + neu senden'}
                </button>
                <button onClick={onAction('unsign')} className={`${itemClass} ${danger}`}>
                  <FileX size={14} /> {t('vertrag.actionUnsign') || 'Unterschrift löschen'}
                </button>
              </>
            )}
            {v.status !== 'storniert' && v.status !== 'entwurf' && (
              <button onClick={onAction('stornieren')} className={`${itemClass} ${danger}`}>
                <Ban size={14} /> {t('vertrag.actionCancel') || 'Stornieren'}
              </button>
            )}
            <div className="my-1 border-t border-[var(--border-default,#E5E7EB)]" />
            <button onClick={() => { onAudit(); setOpenMenuId(null); }} className={`${itemClass} ${normal}`}>
              <History size={14} /> {t('vertrag.auditLogTitle') || 'Audit-Log'}
            </button>
          </div>
        );
        return createPortal(menu, document.body);
      })()}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 bg-[#1A1A2E] text-white text-sm px-4 py-2.5 rounded-xl shadow-sm animate-fade-in">
          <Check size={16} className="text-green-400" /> {toast}
        </div>
      )}

      {/* #1205-F: Vertrag-PDF-Preview Modal (3 Trigger: Wizard / Templates / Liste) */}
      <VertragPreviewModal
        open={!!previewModal}
        mode={previewModal?.mode}
        endpoint={previewModal?.endpoint}
        body={previewModal?.body}
        title={previewModal?.title}
        downloadFileName={previewModal?.downloadFileName}
        onClose={() => setPreviewModal(null)}
      />
    </div>
  );
}
